/**
 * Creating a document — §7.1, §7.2.
 *
 * The form asks for four things and two of them are the ones §7.2 calls the
 * highest-leverage pair in the build: **owner and review date**. "Do not enforce
 * Diátaxis categories on authors. Enforce owner and review date." So the type
 * picker explains itself and moves on, while the owner defaults to the creator
 * and the review date is offered — with its own explanation — the moment the
 * type is one that can carry it.
 *
 * Two rules are the API's and are surfaced rather than reimplemented:
 *
 * - **Type validity by space kind** is a schema check
 *   (`document_enforce_node_rules_trg`), so this warns when a combination will
 *   be refused and still lets the request go. Filtering the picker instead would
 *   put a second copy of §7.1's table in the client, and the copy is what drifts.
 * - **Naming an owner other than yourself is `admin.override`**, not
 *   `node.create`. The field says so; the API decides.
 */

import { useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import {
  useCreateDocument,
  useMe,
  usePeoplePicker,
  useSpaceNodes,
  useSpaces,
} from '../../api/queries.ts';
import { DOCUMENT_TYPES, type DocumentType } from '../../api/types.ts';
import { roleLabel } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { useCreatePrefill } from './navigation.tsx';
import { isReviewable } from './review.tsx';
import { documentTypeDescription, documentTypeLabel, isClientFacing } from './vocabulary.tsx';

export function NewDocumentPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const prefill = useCreatePrefill();
  const me = useMe();
  const create = useCreateDocument();

  const [spaceId, setSpaceId] = useState(prefill.spaceId);
  const [parentNodeId, setParentNodeId] = useState(prefill.parentNodeId);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<DocumentType>('playbook');
  const [ownerId, setOwnerId] = useState('');
  const [reviewBy, setReviewBy] = useState('');

  // `node.create` rather than the default `document.view`: the list should be
  // the spaces this person can actually put something in.
  const spaces = useSpaces('node.create');
  const nodes = useSpaceNodes(spaceId, 'node.create');
  const picker = usePeoplePicker();

  const space = spaces.data?.spaces.find((candidate) => candidate.id === spaceId);
  const typeRefused = space?.kind === 'client' && !isClientFacing(type);

  const spaceOptions = (spaces.data?.spaces ?? []).map((candidate) => ({
    value: candidate.id,
    label: candidate.name,
    description: candidate.kind === 'client' ? 'Client space' : 'Internal space',
  }));

  const parentOptions = [
    { value: '', label: 'None — top level in this space' },
    ...(nodes.data?.nodes ?? []).map((node) => ({ value: node.id, label: node.title })),
  ];

  const ownerOptions = [
    { value: '', label: me.data ? `${me.data.user.name} (you)` : 'You' },
    ...(picker.data?.people ?? [])
      .filter((person) => person.id !== me.data?.user.id)
      .map((person) => ({
        value: person.id,
        label: person.name,
        description: `${person.email} · ${roleLabel(person.role)}`,
      })),
  ];

  const canSubmit = spaceId !== '' && title.trim() !== '';

  return (
    <>
      <PageHeader
        title="New document"
        description="Same editor, same storage, same permissions for every type. What differs is the lifecycle and whether it is ever asked to be re-read."
      />

      <Card className="max-w-2xl">
        <Form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(
              {
                spaceId,
                title: title.trim(),
                type,
                ...(parentNodeId ? { parentNodeId } : {}),
                ...(ownerId ? { ownerId } : {}),
                ...(isReviewable(type) && reviewBy.trim() ? { reviewBy: reviewBy.trim() } : {}),
              },
              {
                onSuccess: (result) => {
                  void navigate({
                    to: '/documents/$nodeId',
                    params: { nodeId: result.document.nodeId },
                  });
                },
              },
            );
          }}
        >
          <TextField label="Title" value={title} onChange={setTitle} isRequired autoFocus />

          <Select
            label="Space"
            placeholder="Choose a space…"
            options={spaceOptions}
            selectedKey={spaceId || null}
            onSelectionChange={(value) => {
              setSpaceId(value);
              // The parent has to live in the same space as the child, so a
              // parent picked under the old space is never valid under the new.
              setParentNodeId('');
            }}
            isRequired
            description="Only spaces you can create in. The space a document sits in is the answer to who can see it."
          />

          <Select
            label="Parent node"
            options={parentOptions}
            selectedKey={parentNodeId}
            onSelectionChange={setParentNodeId}
            isDisabled={spaceId === ''}
            description="A child node must be in the same space as its parent."
          />

          <Select
            label="Type"
            options={DOCUMENT_TYPES.map((value) => ({
              value,
              label: documentTypeLabel(value),
              description: documentTypeDescription(value),
            }))}
            selectedKey={type}
            onSelectionChange={setType}
          />

          {typeRefused ? (
            <Alert tone="warning" title="This type is not valid in a client space">
              §7.1 keeps playbooks, references, and project notes internal — a playbook that needs a
              client audience gets a deliverable written from it, not a reclassification. The schema
              check will refuse this combination.
            </Alert>
          ) : null}

          <Select
            label="Owner"
            options={ownerOptions}
            selectedKey={ownerId}
            onSelectionChange={setOwnerId}
            description="Required and non-null, always. Naming anyone but yourself is an administrative override, not an ordinary create."
          />

          {isReviewable(type) ? (
            <TextField
              label="Review by"
              value={reviewBy}
              onChange={setReviewBy}
              placeholder="YYYY-MM-DD"
              description="A playbook nobody is ever asked to re-read is how a corpus rots. Optional here, and the one field worth filling in."
            />
          ) : (
            <p className="text-sm text-tertiary">
              {documentTypeLabel(type)}s are not on a review cycle — the schema confines review
              dates to playbooks and references.
            </p>
          )}

          {create.isError ? <ErrorState error={create.error} /> : null}

          <div className="flex justify-end gap-3 border-t border-secondary pt-4">
            <Button
              variant="tertiary"
              onPress={() => router.history.back()}
              isDisabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isPending={create.isPending}
              isDisabled={!canSubmit}
            >
              Create document
            </Button>
          </div>
        </Form>
      </Card>
    </>
  );
}
